import { Flex, Icon } from "@theorgchartdev/storybook";
import * as StateLib from "@/Utils/StateLib";
import {
  enumCellRefType,
  enumChartElement,
  kBoxShapes,
  kBoxShapeCodes,
  kBorderStyleCodes,
  kBorderDashCodes,
  enumBranchStyle,
  kLineDashCodes,
  kFontPickerOptions,
  kFontSizeOptions,
  kTextWrapOptions,
  kLabelOptions,
  enumPinCmds,
  kArrangeCmds,
  enumWrapTextMode
} from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { getHoverTip } from "@coreapp/utils/getHoverTip";
import { appstr } from "@coreapp/utils/appstr";

// BOX LAYOUT EDITOR GATEWAY AND UTILS
export const DoBoxLayoutEditor = (targetLayout, previewObj = null, editBoxStyle = false) => {
  const wsMgr = StateLib.GetWSMgr();
  wsMgr.DoBoxLayoutEditor(targetLayout, previewObj, editBoxStyle);
  StateLib.ReRenderCanvas();
};

export const EnableCellControls = () => {
  const wsMgr = StateLib.GetWSMgr();

  let leftInsCol = false;
  let rightInsCol = false;
  let topInsRow = false;
  let bottomInsRow = false;
  let enableDelCols = false;
  let enableDelRows = false;
  let enableMergeAllCells = false;
  let enableUnmergeAllCells = false;
  let enableMergeAny = false;

  let enableDict = {
    enableMergeAny,
    enableMergeAllCells,
    enableUnmergeAllCells,
    topInsRow,
    bottomInsRow,
    leftInsCol,
    rightInsCol,
    enableDelCols,
    enableDelRows
  };

  if (!wsMgr) {
    return enableDict;
  }

  const currentPanel = wsMgr.GetCurrentPanel();
  const selectedCells = currentPanel?.GetSelectionList(enumChartElement.boxCell);
  const firstSelectedGutter = wsMgr.GetFirstSelectedElement(enumChartElement.gutterCell);
  const firstSelectedCell = wsMgr.GetFirstSelectedElement(enumChartElement.boxCell);

  const isGutterCell = firstSelectedGutter != null;
  const selectedElem = wsMgr.GetFirstSelectedElement(null);

  if (selectedElem != null && !isGutterCell) {
    enableDict.leftInsCol = wsMgr.GetOuterSelectionColumn(true) >= 0;
    enableDict.rightInsCol = wsMgr.GetOuterSelectionColumn(false) >= 0;
    enableDict.topInsRow = wsMgr.GetOuterSelectionRow(true) >= 0;
    enableDict.bottomInsRow = wsMgr.GetOuterSelectionRow(false) >= 0;
    enableDict.enableDelCols = wsMgr.GetDeleteColumns() != null;
    enableDict.enableDelRows = wsMgr.GetDeleteRows() != null;

    if (firstSelectedCell) {
      const cellDesc = firstSelectedCell.GetCellDesc();
      if (cellDesc.IsMergedCell()) enableDict.enableUnmergeAllCells = true;
    }

    if (selectedCells.length > 1) {
      enableDict.enableMergeAllCells = true;
    }

    enableDict.enableMergeAny = enableDict.enableMergeAllCells || enableDict.enableUnmergeAllCells;
  }
  return enableDict;
};

// BOX FORMATTING ===================================

// Must match the indeces of kBoxShapes
const BoxShapeIconName = [
  "sharp-rectangle",
  "rectangle",
  "sharp-corner-rectangle",
  "round-corner-rectangle"
];
export const BuildBoxShapeOptions = (UpdateBoxShape, includeLabel = false) => {
  let shapeOptions = [];
  for (let i = 0; i < 4; i++) {
    var boxShape = kBoxShapes[i];
    const shapeCode = kBoxShapeCodes[i];
    const label = Globals._stringMgr ? getHoverTip(shapeCode) : "";
    const iconName = BoxShapeIconName[i];
    let shapeOption = {};
    shapeOption.content = (
      <Flex gap="0.5rem" alignItems="center">
        <Icon name={iconName} />
        {includeLabel ? label : <span className="oc-sr-only">{label}</span>}
      </Flex>
    );
    shapeOption.value = boxShape;
    shapeOption.clickHandler = UpdateBoxShape;
    shapeOptions.push(shapeOption);
  }
  return shapeOptions;
};

export const BuildBorderWeightOptions = UpdateBorderWeight => {
  let borderWeightOptions = [];
  //weight - pixel value
  for (let y = 0; y < 4; y++) {
    let weightOption = {};
    let value = y + 1;
    weightOption.label = value.toString() + "px";
    weightOption.value = y + 1;
    weightOption.clickHandler = UpdateBorderWeight;
    borderWeightOptions.push(weightOption);
  }
  return borderWeightOptions;
};

// Must match the indeces of kBorderStyleCodes
const BorderStyleIconNames = [null, "border-single", "border-double"];
export const BuildBorderStyleOptions = UpdateBorderStyle => {
  //style - no border, single border, double border text
  let borderStyleOptions = [];
  for (var y = 0; y < 3; y++) {
    var theCode = kBorderStyleCodes[y];
    const iconName = BorderStyleIconNames[y];
    let styleOption = {};
    styleOption.content = (
      <Flex gap="0.5rem" alignItems="baseline" justifyContent="space-between" width="120px">
        {Globals._stringMgr ? appstr(theCode) : ""}
        {iconName ? <Icon name={iconName} size="small" /> : null}
      </Flex>
    );
    styleOption.label = kBorderStyleCodes[y];
    styleOption.value = y;
    styleOption.clickHandler = UpdateBorderStyle;
    borderStyleOptions.push(styleOption);
  }
  return borderStyleOptions;
};

// Must match the indeces of kBorderDashCodes
const BorderDashIconName = ["border-solid", "border-dashed"];
export const BuildBorderDashOptions = updateBorderDash => {
  //dash - icon single line, dotted line
  let borderDashOptions = [];
  for (var y = 0; y < 2; y++) {
    const theCode = kBorderDashCodes[y];
    const iconName = BorderDashIconName[y];
    let styleOption = {};
    styleOption.content = (
      <Flex gap="0.5rem" justifyContent="space-between" width="120px">
        {Globals._stringMgr ? appstr(theCode) : ""}
        <Icon name={iconName} size="small" />
      </Flex>
    );
    styleOption.label = kBorderDashCodes[y];
    styleOption.value = y;
    styleOption.clickHandler = updateBorderDash;
    borderDashOptions.push(styleOption);
  }
  return borderDashOptions;
};

// BRANCH FORMATTING ===========================================

// Must match the keys of enumBranchStyle
export const BranchStyleIconNames = {
  tree: "branch-tree",
  stagger: "branch-stagger",
  banded: "branch-banded",
  twocolumn: "branch-twocol",
  fourcolumn: "branch-fourcol",
  sixcolumn: "branch-sixcol",
  eightcolumn: "branch-eightcol",
  twelvecolumn: "branch-twelvecol",
  sixteencolumn: "branch-sixteencol",
  twentycolumn: "branch-twentycol",
  onecolumncenterright: "branch-onecolcenterright",
  onecolumncenterleft: "branch-onecolcenterleft",
  onecolumnright: "branch-onecolright",
  onecolumnleft: "branch-onecolleft",
  swimlane: "branch-swimlane",
  assistantpeerright: "branch-assistantpeerright",
  assistantpeerleft: "branch-assistantpeerleft",
  assistantright: "branch-assistantright",
  assistantleft: "branch-assistantleft"
};

export const BuildBranchStyleOptions = (
  UpdateBranchStyle,
  includeLabel = false,
  dataTestIdPrefix = undefined
) => {
  let branchStyleOptions = [];

  for (var aBranchStyle in enumBranchStyle) {
    if ([enumBranchStyle.banded, enumBranchStyle.swimlane].includes(enumBranchStyle[aBranchStyle]))
      continue;
    let disabled = false;
    let branchOption = {};
    if (enumBranchStyle[aBranchStyle] > -1) {
      const styleCode = enumBranchStyle[aBranchStyle];
      const iconName = BranchStyleIconNames[aBranchStyle];
      const label = Globals._stringMgr ? getHoverTip(aBranchStyle) : "";
      branchOption.content = (
        <Flex gap="0.5rem" alignItems="center">
          <Icon name={iconName} />
          {includeLabel ? label : <span className="oc-sr-only">{label}</span>}
        </Flex>
      );
      if (dataTestIdPrefix) {
        branchOption.dataTestId = `${dataTestIdPrefix}-${aBranchStyle}`;
      }
      branchOption.label = label;
      branchOption.value = aBranchStyle;
      branchOption.clickHandler = UpdateBranchStyle;
      if (styleCode === 18 || styleCode === 19) disabled = true;
      branchStyleOptions.push(branchOption);
    }
  }
  return branchStyleOptions;
};

// LINE FORMATTING ==============================================

// Must match the indeces of kLineDashCodes
const LineStyleIconName = ["border-solid", "border-dashed", null];
export const BuildLineStyleOptions = UpdateLineWeight => {
  let lineDashOptions = [];
  for (let i = 0; i < kLineDashCodes.length; i++) {
    let code = kLineDashCodes[i];
    let lineWeightOption = {};
    const iconName = LineStyleIconName[i];
    lineWeightOption.value = i;
    lineWeightOption.content = (
      <Flex gap="0.5rem" alignItems="baseline" justifyContent="space-between" width="90px">
        {Globals._stringMgr ? appstr(code) : ""}
        {iconName ? <Icon name={iconName} size="small" /> : null}
      </Flex>
    );
    lineWeightOption.clickHandler = UpdateLineWeight;
    lineDashOptions.push(lineWeightOption);
  }
  return lineDashOptions;
};

export const BuildLineSubGroupOptions = UpdateLineSubGroup => {
  let lineSubGroupOptions = BuildLineStyleOptions(UpdateLineSubGroup);
  // let clearOption = {};
  // clearOption.value = null;
  // clearOption.label = Globals._stringMgr ? appstr("clearsubgroup") : "";
  // clearOption.clickHandler = UpdateLineSubGroup;
  // lineSubGroupOptions.push(clearOption);
  return lineSubGroupOptions;
};

// TEXT FORMATTING ===============================================
export const BuildFontNameOptions = UpdateFontName => {
  let fontNames = [];
  for (let i = 0; i < kFontPickerOptions.length; i++) {
    let fontName = kFontPickerOptions[i];
    let fontOption = {};
    fontOption.content = (
      <div style={{ fontFamily: fontName, fontSize: "14px", color: "#3d3d4a" }}>{fontName}</div>
    );
    fontOption.value = fontName;
    fontOption.clickHandler = UpdateFontName;
    fontNames.push(fontOption);
  }
  return fontNames;
};

export const BuildFontSizeOptions = UpdateFontSize => {
  let fontSizes = [];
  for (let i = 0; i < kFontSizeOptions.length; i++) {
    var fontSize = kFontSizeOptions[i];
    let fontOption = {};
    fontOption.label = fontSize;
    fontOption.value = parseInt(fontSize);
    fontOption.clickHandler = UpdateFontSize;
    fontSizes.push(fontOption);
  }
  return fontSizes;
};

// NOTE: Used for BuildHorzAlignmentOptions custom content
const HorzAlignLabelKeys = {
  left: "alignleft",
  center: "aligncenter",
  right: "alignright",
  justify: "Justify"
};
export const BuildHorzAlignmentOptions = UpdateHorzAlignment => {
  let horzAlignmentOptions = [];
  const alignList = ["align-left", "align-center", "align-right", "align-justify"];
  for (let i = 0; i < alignList.length; i++) {
    let alignOption = alignList[i];
    let option = {};
    option.value = alignOption.split("-")[1];
    const label = Globals._stringMgr ? appstr(HorzAlignLabelKeys[option.value]) : "";
    option.content = (
      <Flex alignItems="center">
        <Icon name={alignOption} size={"small"} />
        <span className="oc-sr-only">{label}</span>
      </Flex>
    );
    option.clickHandler = UpdateHorzAlignment;
    horzAlignmentOptions.push(option);
  }
  return horzAlignmentOptions;
};

export const BuildVertAlignmentOptions = UpdateVertAlignment => {
  let vertAlignmentOptions = [];
  const alignList = ["Top", "Middle", "Bottom"];
  for (let i = 0; i < alignList.length; i++) {
    let alignOption = alignList[i];
    let option = {};
    option.label = appstr(alignOption).toLowerCase();
    option.value = alignOption.toLowerCase();
    option.clickHandler = UpdateVertAlignment;
    vertAlignmentOptions.push(option);
  }
  return vertAlignmentOptions;
};

export const BuildCellWrapTextOptions = UpdateCellWrapText => {
  let cellWrapOptions = [];
  for (let i = 0; i < kTextWrapOptions.length; i++) {
    let code = kTextWrapOptions[i];
    let value = enumWrapTextMode[code];
    let label = Globals._stringMgr ? appstr(code) : "";
    let cellWrapOption = {};
    cellWrapOption.label = label;
    cellWrapOption.value = value;
    cellWrapOption.clickHandler = UpdateCellWrapText;
    cellWrapOptions.push(cellWrapOption);
  }
  return cellWrapOptions;
};

export const BuildFieldDisplayModeOptions = UpdateFieldDisplayMode => {
  let fieldDisplayOptions = [];
  for (let i = 0; i < kLabelOptions.length; i++) {
    let code = kLabelOptions[i];
    let value = i;
    let label = Globals._stringMgr ? appstr(code) : "";
    let fieldDisplayOption = {};
    fieldDisplayOption.label = label;
    fieldDisplayOption.value = value;
    fieldDisplayOption.clickHandler = UpdateFieldDisplayMode;
    fieldDisplayOptions.push(fieldDisplayOption);
  }
  return fieldDisplayOptions;
};

export const formulaScopes = [
  { label: "scopechart", value: enumCellRefType.topbox, hover: "chartScope" },
  { label: "scopebranch", value: enumCellRefType.branch, hover: "branchScope" }
];

export const BuildFormualScopeOptions = clickHandler => {
  let options = [];
  for (const scope of formulaScopes) {
    let option = {};
    option.label = appstr(scope.label);
    option.value = scope.value;
    option.clickHandler = clickHandler;
    option.dataTestId = `ctx-toolbar-formula-scope-${scope.label}`;
    options.push(option);
  }

  return options;
};

// ARRANGE, PIN, & SCALE
// scaling options and pin options not supported yet
export const BuildPinOptions = clickHandler => {
  let options = [];
  for (let pin of enumPinCmds) {
    let option = {};
    option.label = getHoverTip(pin);
    option.value = pin;
    option.clickHandler = clickHandler;
    option.divider = pin === enumPinCmds[5] || pin === enumPinCmds[2];
    options.push(option);
  }
  return options;
};

export const BuildArrangeOptions = clickHandler => {
  let options = [];
  for (let arrange of kArrangeCmds) {
    let option = {};
    option.label = appstr(arrange);
    option.value = arrange;
    option.clickHandler = clickHandler;
    options.push(option);
  }
  return options;
};
